"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.adminPrizeContract = exports.ZUpdatePrizeRequest = exports.ZCreatePrizeRequest = void 0;
const dbEnums_1 = require("@daymade/common-lib/dbEnums");
const core_1 = require("@ts-rest/core");
const zod_1 = require("zod");
const utils_1 = require("../utils");
const models_1 = require("./models");
const c = (0, core_1.initContract)();
exports.ZCreatePrizeRequest = models_1.ZPrizeInsert.extend({
    title: models_1.ZPrizeInsert.shape.title.trim().min(1, {
        message: 'Title must contain at least 1 non-whitespace character.',
    }),
    subheading: models_1.ZPrizeInsert.shape.subheading.trim().min(1, {
        message: 'Subheading must contain at least 1 non-whitespace character.',
    }),
    slug: models_1.ZPrizeInsert.shape.slug
        .min(1, 'Slug must have at least 1 character')
        .refine((slug) => {
        const sanitizedSlug = slug.replace(/[^\w-]|_/g, '');
        return slug === sanitizedSlug;
    }, {
        message: 'Slug contains invalid characters. Only alphanumeric characters and hyphens are allowed.',
    }),
    imageLocations: models_1.ZPrizeInsert.shape.imageLocations.extend({
        main: models_1.ZPrizeInsert.shape.imageLocations.shape.main
            .trim()
            .url('Main image is required and must have a valid URL'),
    }),
    whatsIncludedBody: models_1.ZPrizeInsert.shape.whatsIncludedBody.refine((html) => {
        const text = html.replace(/<[^>]*>/g, '').trim();
        return text.length !== 0;
    }, {
        message: "What's Included Body must not be empty.",
    }),
    seoKeywords: models_1.ZPrizeInsert.shape.seoKeywords.trim().min(1, {
        message: 'SEO Keywords must contain at least 1 non-whitespace character.',
    }),
    seoImageAltText: models_1.ZPrizeInsert.shape.seoImageAltText.extend({
        main: models_1.ZPrizeInsert.shape.seoImageAltText.shape.main.trim().min(1, {
            message: 'Main Image Alt Text must contain at least 1 non-whitespace character',
        }),
    }),
    vendorCategory: models_1.ZPrizeInsert.shape.vendorCategory.refine((value) => value !== null, {
        message: 'Required',
    }),
    products: models_1.ZProductInsert.extend({
        name: models_1.ZProductInsert.shape.name.trim().min(1, {
            message: 'Product name must contain at least 1 non-whitespace character.',
        }),
        price: models_1.ZProductInsert.shape.price.refine((price) => price && price > 0, {
            message: 'Product price is required and must be greater than 0.',
        }),
    }).array(),
    categories: zod_1.z.string().array(),
    tiers: zod_1.z.record(zod_1.z.string(), zod_1.z.string().nullable()),
});
exports.ZUpdatePrizeRequest = exports.ZCreatePrizeRequest.partial().required({
    id: true,
});
exports.adminPrizeContract = c.router({
    createPrize: {
        method: 'POST',
        path: '/',
        body: exports.ZCreatePrizeRequest,
        responses: {
            200: zod_1.z.object({ id: zod_1.z.number(), slug: zod_1.z.string() }),
            400: c.type(),
        },
        summary: 'Create a prize',
    },
    updatePrize: {
        method: 'PATCH',
        path: '/',
        body: exports.ZUpdatePrizeRequest,
        responses: {
            200: utils_1.successSchema,
            400: c.type(),
        },
        summary: 'Update a prize',
    },
    getPrize: {
        method: 'GET',
        path: '/details/:id',
        pathParams: zod_1.z.object({
            id: zod_1.z.coerce.number(),
        }),
        responses: {
            200: models_1.ZPrizeSelect.omit({
                createdAt: true,
                updatedAt: true,
                legacyPrizeValue: true,
            }).extend({
                products: models_1.ZProductSelect.omit({
                    createdAt: true,
                    updatedAt: true,
                }).array(),
                categories: zod_1.z.string().array(),
                tiers: zod_1.z.record(zod_1.z.string(), zod_1.z.string()),
            }),
            404: zod_1.z.object({
                message: zod_1.z.string(),
            }),
        },
        summary: 'Get a prize by id',
    },
    getPrizes: {
        method: 'GET',
        path: '/prizes',
        responses: {
            200: models_1.ZPrizeCore.merge(models_1.ZPrizeSelect.pick({ status: true }))
                .extend({
                tiers: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
            })
                .array(),
        },
        summary: 'Get all prizes with prize tiers',
    },
    getPrizesInPrizeTier: {
        method: 'GET',
        path: '/prize-tier/:id/prizes',
        pathParams: zod_1.z.object({
            id: zod_1.z.coerce.number(),
        }),
        responses: {
            200: models_1.ZPrizeCore.merge(models_1.ZPrizeSelect.pick({
                status: true,
                overviewBody: true,
                whatsIncludedBody: true,
            }).extend({
                lastUsedInDraw: zod_1.z
                    .object({
                    drawSchedule: zod_1.z.string(),
                    lastUsedAt: zod_1.z.coerce.date().nullable(),
                })
                    .array(),
            })).array(),
        },
        summary: 'Get prizes in a prize tier',
    },
    getPrizesInDisplayGroups: {
        method: 'GET',
        path: '/prize-display-ordering/prizes',
        query: zod_1.z.object({
            groups: models_1.ZPrizeDisplayGroup.array(),
            status: zod_1.z.nativeEnum(dbEnums_1.PrizeStatus).optional(),
            prizeTierId: zod_1.z.coerce.number().optional(),
        }),
        responses: {
            200: zod_1.z.record(models_1.ZPrizeDisplayGroup, models_1.ZPrizeCore.extend({
                status: zod_1.z.nativeEnum(dbEnums_1.PrizeStatus),
                position: zod_1.z.number(),
            }).array()),
        },
        summary: 'Get prizes in display groups',
    },
    getAllCategories: {
        method: 'GET',
        path: '/get-all-categories',
        responses: {
            200: zod_1.z.record(zod_1.z.string(), zod_1.z.string().array()),
        },
        summary: 'Get all prize categories, grouped by main category',
    },
    getPrizeTiers: {
        method: 'GET',
        path: '/get-prize-tiers',
        query: zod_1.z.object({
            prizeStructure: zod_1.z.string().optional(),
            isRotatable: utils_1.booleanParamSchema.optional(),
        }),
        responses: {
            200: zod_1.z
                .object({
                id: zod_1.z.number(),
                prizeStructure: zod_1.z.string(),
                prizeTier: zod_1.z.string(),
            })
                .array(),
        },
        summary: 'Get prize tiers',
    },
    uploadPrizePhoto: {
        method: 'POST',
        path: '/upload-prize-photo',
        contentType: 'multipart/form-data',
        body: c.type(),
        responses: {
            200: zod_1.z.string(),
            400: zod_1.z.object({
                message: zod_1.z.string(),
            }),
        },
    },
    addPrizeToDisplayGroup: {
        method: 'POST',
        path: '/prize-display-ordering/:group/prize/:prizeId',
        pathParams: zod_1.z.object({
            group: models_1.ZPrizeDisplayGroup,
            prizeId: zod_1.z.coerce.number(),
        }),
        body: zod_1.z.object({}),
        responses: {
            200: utils_1.successSchema,
        },
    },
    removePrizeFromDisplayGroup: {
        method: 'DELETE',
        path: '/prize-display-ordering/:group/prize/:prizeId',
        pathParams: zod_1.z.object({
            group: models_1.ZPrizeDisplayGroup,
            prizeId: zod_1.z.coerce.number(),
        }),
        responses: {
            200: utils_1.successSchema,
        },
    },
    reorderPrizeInDisplayGroup: {
        method: 'POST',
        path: '/prize-display-ordering/:group/prize/:prizeId/reorder',
        pathParams: zod_1.z.object({
            group: models_1.ZPrizeDisplayGroup,
            prizeId: zod_1.z.coerce.number(),
        }),
        body: zod_1.z.object({
            position: zod_1.z.coerce.number(),
        }),
        responses: {
            200: utils_1.successSchema,
        },
    },
}, {
    pathPrefix: '/admin',
    commonResponses: {
        401: c.type(),
        403: c.type(),
    },
});
//# sourceMappingURL=prize.admin.contract.js.map